import {
  ApiBlueprint,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  storageApiRef,
} from '@backstage/frontend-plugin-api';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';

/**
 * Moves the `storageApi` off the browser and onto the backend.
 *
 * The default implementation the app gets from `@backstage/frontend-defaults`
 * is `WebStorage`, which is `localStorage`: starred entities, the sidebar pin
 * state, the picked theme and the per-user table settings never left the
 * device they were set on. `@backstage/plugin-user-settings-backend` was
 * already a declared dependency of `packages/backend`, but it was never passed
 * to `backend.add()`, so there was nothing on the other end to talk to — the
 * dependency was inert. It is registered now, and this extension is the
 * frontend half.
 *
 * `plugin-user-settings` does **not** wire this up on its own: its `/alpha`
 * module contributes the four settings pages and nothing else, and
 * `UserSettingsStorage` is exported from the package's main entry point for the
 * app to opt into. That is why this lives here and not in `app-config.yaml`.
 *
 * `UserSettingsStorage.create` builds a `WebStorage` of its own as a fallback
 * and uses it whenever the user is not signed in, so an anonymous or
 * mid-sign-in session still has a working `storageApi` rather than a rejected
 * promise.
 *
 * One-time effect on rollout: settings already in a user's `localStorage` are
 * not migrated. A signed-in user reads from the backend, which starts empty, so
 * the first load after deploy shows no starred entities and the default theme.
 * Re-starring is the only recovery — there is no upstream migration path.
 *
 * `signalApi` is deliberately omitted. It is optional in `create`, and passing
 * it would make settings changes propagate live between a user's open tabs and
 * devices over the signals connection. Enabling that means adding
 * `@backstage/plugin-signals-react` to this package's dependencies for
 * `signalApiRef`; without it, changes are still persisted and still read back
 * on the next load, just not pushed to sessions that are already open.
 */
export const userSettingsStorageApi = ApiBlueprint.make({
  name: 'user-settings-storage',
  params: defineParams =>
    defineParams({
      api: storageApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        errorApi: errorApiRef,
        fetchApi: fetchApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ discoveryApi, errorApi, fetchApi, identityApi }) =>
        UserSettingsStorage.create({
          discoveryApi,
          errorApi,
          fetchApi,
          identityApi,
        }),
    }),
});
